import { Theme } from './theme';
import { mqFactory } from './mediaQueries';
import { getLinearGradient } from './gradients';

/**
 * Default theme settings for all sites, used by Emotion’s `ThemeProvider`.
 * Can be overridden by the corresponding `styles/themes/theme.ts` file per site.
 */
const defaultBreakpoints = [
  {
    name: 'sm',
    width: 576,
  },
  {
    name: 'md',
    width: 768,
  },
  {
    name: 'lg',
    width: 992,
  },
  {
    name: 'xl',
    width: 1280,
  },
  {
    name: 'xxl',
    width: 1920,
  },
];

/** Color tokens */
const colors = {
  brand: {
    green160: '#005b4b',
    green140: '#007a6a', // link
    green120: '#00856a', // button
    green100: '#00977d',
    green80: '#33ac97',
    green60: '#66c1b1',
    green40: '#99d5cb',
    green20: '#cceae5',
    orange160: '#661e0a',
    orange140: '#992d0f',
    orange120: '#cc3c14',
    orange100: '#ff4b19',
    orange80: '#ff6f47',
    orange60: '#ff9375',
    orange40: '#ffb7a3',
    orange20: '#ffdbd1',
  },
  accent: {
    green160: '#153600',
    green140: '#1f5101',
    green120: '#2a6c01',
    green100: '#348701',
    green80: '#5d9f34',
    green60: '#85b767',
    green40: '#aecf99',
    green20: '#d6e7cc',
    blue160: '#003246',
    blue140: '#014c6a',
    blue120: '#01658d',
    blue100: '#017eb0',
    blue80: '#3498c0',
    blue60: '#67b2d0',
    blue40: '#99cbdf',
    blue20: '#cce5ef',
    red160: '#56190e',
    red140: '#802616',
    red120: '#ab321d',
    red100: '#d63f24',
    red80: '#de6550',
    red60: '#e68c7c',
    red40: '#efb2a7',
    red20: '#f7d9d3',
    yellow160: '#664300',
    yellow140: '#996500',
    yellow120: '#cc8600',
    yellow100: '#ffa800',
    yellow80: '#ffb933',
    yellow60: '#ffcb66',
    yellow40: '#ffdc99',
    yellow20: '#fec',
  },
  neutral: {
    black32: '#00000052', // 32% opacity
    black100: '#000',
    white100: '#fff',
    grey160: '#222222', // text default
    grey140: '#595959', // text light
    grey120: '#767676',
    grey100: '#949494',
    grey80: '#c8c8c8',
    grey60: '#dadada',
    grey40: '#e8e8e8',
    grey20: '#f3f3f3',
  },
} as const;

/** The FLOYT theme */
const theme: Theme = {
  colors,

  gradient: {
    green: { from: colors.brand.green120, to: colors.brand.green160 },
    orange: { from: colors.brand.orange100, to: colors.brand.orange60 },
    mint: { from: colors.brand.green60, to: colors.brand.green80 },
    mintAlt: { from: colors.brand.green60, to: colors.brand.green100 },
    brand: {
      from: colors.brand.orange100,
      via: colors.brand.orange40,
      to: colors.brand.green60,
    },
  },

  fontFamily: {
    default: "'Source Sans Pro', ui-sans-serif, system-ui, sans-serif",
    alternate:
      "Quicksand, Bryant, 'Proxima Nova Soft', Arial Rounded MT Bold, sans-serif",
  },
  fontWeight: {
    body: 'normal',
    heading: 'bold',
  },
  fontSize: {
    xl: {
      size: '3rem',
      lineHeight: '1.2',
    },
    lg: {
      size: '2rem',
      lineHeight: '1.2',
    },
    md: {
      size: '1.5rem',
      lineHeight: '1.2',
    },
    sm: {
      size: '1rem',
      lineHeight: '1.5',
    },
    xs: {
      size: '0.875rem',
      lineHeight: '1.5',
    },
    xxs: {
      size: '0.75rem',
      lineHeight: '1.5',
    },
  },
  spacing: {
    0: '0',
    5: '0.125rem',
    10: '0.25rem',
    20: '0.5rem',
    30: '0.75rem',
    40: '1rem',
    50: '1.5rem',
    60: '2rem',
    70: '2.5rem',
    80: '3rem',
    90: '4rem',
    100: '4.5rem',
    110: '6rem',
    120: '7rem',
    130: '8rem',
    140: '9rem',
    150: '10rem',
  },
  elevation: {
    low: '0 0.375rem 1.25rem rgb(0 0 0 / 6%)',
    medium: '0 0.625rem 1.5rem rgb(0 0 0 / 8%)',
    high: '0 0.625rem 2rem rgb(0 0 0 / 14%)',
  },
  radius: {
    none: '0',
    xxs: '0.125rem', // 2px
    xs: '0.25rem', // 4px
    sm: '0.5rem', // 8px
    md: '0.75rem', // 12px
    lg: '1rem', // 16px
    round: '5rem', // some arbitrary high value but not 100% to have fully rounded corners
  },
  mq: mqFactory(defaultBreakpoints),
  linearGradient: getLinearGradient,
};

export { theme };
